import { useState } from 'react';

import { Button, Group, Text } from '@mantine/core';

import { ADD_ENCOUNTER_VALUE } from '@/utils/reportAppointmentEligibility';

import { LabelAndValue } from '@/types/custom';
import Dropdown from '@/ui/Dropdown';
import TextInputWrapper from '@/ui/TextInputWrapper';

export type EncounterOption = LabelAndValue & {
  appointmentId?: string;
};

export const EncounterIdField = ({
  value,
  options,
  disabled,
  requireSelection = true,
  emptyMessage = 'Select an encounter to sign the report.',
  onSelect,
  onSave
}: {
  value: string;
  options: EncounterOption[];
  disabled?: boolean;
  requireSelection?: boolean;
  emptyMessage?: string;
  onSelect: (option: EncounterOption) => void;
  onSave: (encounterId: string) => Promise<void> | void;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const choices: EncounterOption[] = [
    ...(value && !options.some((option) => option.value === value)
      ? [{ value, label: value }]
      : []),
    ...options,
    { value: ADD_ENCOUNTER_VALUE, label: 'Add an encounter' }
  ];
  const selected = choices.find((option) => option.value === value);

  const save = async () => {
    const next = draft.trim();
    if (!next) return;
    setIsSaving(true);
    try {
      await onSave(next);
      setIsAdding(false);
      setDraft('');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {isAdding ? (
        <>
          <TextInputWrapper
            labelProps={{ variant: 'sentence' }}
            value={draft}
            placeholder="Enter encounter ID"
            onChange={(event) => setDraft(event.target.value)}
          />
          <Group gap={8} mt={6}>
            <Button size="xs" loading={isSaving} disabled={!draft.trim()} onClick={save}>
              Save
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={isSaving}
              onClick={() => {
                setIsAdding(false);
                setDraft('');
              }}>
              Cancel
            </Button>
          </Group>
        </>
      ) : (
        <Dropdown
          options={choices}
          isMulti={false}
          value={selected}
          isDisabled={disabled}
          placeholder="Select an encounter"
          onChange={(option) => {
            if (!option || Array.isArray(option)) return;
            if (option.value === ADD_ENCOUNTER_VALUE) {
              setDraft('');
              setIsAdding(true);
              return;
            }
            onSelect(option as EncounterOption);
          }}
        />
      )}
      {requireSelection && !value && !isAdding && !disabled && (
        <Text c="red" size="xs" mt={4}>
          {emptyMessage}
        </Text>
      )}
    </div>
  );
};
